import { AnchorHTMLAttributes, ReactNode } from 'react'
import './SkipLink.css'

export interface SkipLinkProps extends AnchorHTMLAttributes<HTMLAnchorElement> {
  /** Target element ID (with hash) */
  href: string
  /** Link text, defaults to "Skip to main content" */
  children?: ReactNode
}

/**
 * Sanitizes href to prevent XSS
 */
function sanitizeHref(href: string | undefined): string {
  if (!href || typeof href !== 'string') return '#main-content'
  if (href.startsWith('javascript:') || href.startsWith('data:')) {
    return '#main-content'
  }
  // Ensure it's an anchor link
  if (!href.startsWith('#')) {
    return `#${href}`
  }
  return href
}

/**
 * SkipLink - Accessibility skip navigation link
 *
 * Allows keyboard users to skip repetitive navigation and jump
 * directly to main content. Hidden until focused.
 *
 * Usage:
 * ```tsx
 * <SkipLink href="#main-content" />
 * // Or with custom text:
 * <SkipLink href="#nav">Skip to navigation</SkipLink>
 * ```
 *
 * Derek-friendly: Doesn't interfere with normal use
 * Nate-friendly: Fast keyboard navigation
 */
export default function SkipLink({
  href,
  children = 'Skip to main content',
  className = '',
  ...props
}: SkipLinkProps) {
  const sanitizedHref = sanitizeHref(href)
  const linkClasses = ['ps-skip-link', className].filter(Boolean).join(' ')

  return (
    <a
      href={sanitizedHref}
      className={linkClasses}
      {...props}
    >
      {children}
    </a>
  )
}
